import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

const SCROLL_START = 'top 80%';
/** Delay between sibling text reveals in the same section */
const SIBLING_GAP = 0.28;
const MAGNETIC_STRENGTH = 0.4;

/**
 * Promoter Socials GSAP — patterns from /Sites/gsap (gsap.test):
 * - .fx-char-stagger  → character stagger (preheading)
 * - .fx-line-mask     → line mask reveal (heading)
 * - .fx-multiline     → multi-line reveal (paragraph / sub_text)
 * - .promoter-socials-btn → fade in (+ magnetic on whole ticket <a>)
 * - .accordion__item  → fade in up (FAQ rows)
 */
export function promoterSocialsText() {
    setupHeaderScroll();

    const root = document.querySelector('.promoter-socials-page');
    if (!root) return;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    const init = () => {
        const sequencedButtons = setupTextBlocks(root);
        setupAccordionFadeIn(root);
        setupProgrammeItems(root);
        setupAttendeesItems(root);
        setupVideoItems(root);
        setupVideosTicker(root);
        setupButtonFadeIn(root, sequencedButtons);
        setupMagneticButtons(root);
        setupScrollIndicators(root);
        setupQuoteWordFill(root);
        setupQuoteImageReveal(root);
        setupEventCards(root);
        ScrollTrigger.refresh();
    };

    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(init).catch(init);
    } else {
        init();
    }

    // LazyLoad swaps placeholder images for real ones after GSAP calculates trigger
    // positions, causing all triggers to be shifted. Watch body height and refresh.
    if (typeof ResizeObserver !== 'undefined') {
        let refreshTimer;
        const bodyObserver = new ResizeObserver(() => {
            clearTimeout(refreshTimer);
            refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 80);
        });
        bodyObserver.observe(document.body);
        // Disconnect once images have had time to settle
        setTimeout(() => bodyObserver.disconnect(), 8000);
    }
}

/**
 * Section text timeline (preheading → heading → multiline).
 * CTA buttons fade in after the last text tween (">-0.4" overlap).
 * Returns buttons already sequenced so header CTAs stay independent.
 */
function setupTextBlocks(root) {
    const sequencedButtons = new Set();
    const sections = gsap.utils.toArray(root.querySelectorAll('section'));

    sections.forEach((section) => {
        const items = gsap.utils
            .toArray(section.querySelectorAll('.fx-char-stagger, .fx-line-mask, .fx-multiline'))
            .filter((el) => el.textContent && el.textContent.trim());

        if (!items.length) return;

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: items[0],
                start: SCROLL_START,
                toggleActions: 'play none none none',
            },
        });

        items.forEach((el, index) => {
            const at = index * SIBLING_GAP;

            if (el.classList.contains('fx-char-stagger')) {
                addCharStagger(el, tl, at);
            } else if (el.classList.contains('fx-line-mask')) {
                addLineMask(el, tl, at);
            } else if (el.classList.contains('fx-multiline')) {
                addMultiline(el, tl, at);
            }
        });

        // Start CTA near the end of the last text tween (overlap, not full wait)
        const buttons = gsap.utils.toArray(section.querySelectorAll('a.promoter-socials-btn'));
        buttons.forEach((btn) => {
            sequencedButtons.add(btn);
            addButtonFadeToTimeline(btn, tl, '>-0.4');
        });
    });

    return sequencedButtons;
}

function addCharStagger(el, tl, at) {
    const split = new SplitText(el, {
        type: 'chars',
        charsClass: 'split-char',
    });

    tl.from(
        split.chars,
        {
            y: 16,
            opacity: 0,
            duration: 0.8,
            ease: 'back.out(1.7)',
            stagger: 0.03,
        },
        at,
    );
}

function addLineMask(el, tl, at) {
    const split = new SplitText(el, {
        type: 'lines',
        linesClass: 'fx-line-mask-box',
    });

    split.lines.forEach((line) => {
        line.innerHTML = `<span>${line.innerHTML}</span>`;
    });

    const inners = el.querySelectorAll('.fx-line-mask-box > span');
    if (!inners.length) return;

    tl.from(
        inners,
        {
            yPercent: 100,
            duration: 0.9,
            ease: 'power4.out',
            stagger: 0.08,
        },
        at,
    );
}

function multilineTargets(el) {
    const paragraphs = el.querySelectorAll(':scope > p');
    if (paragraphs.length) {
        return Array.from(paragraphs);
    }
    return [el];
}

/** Multi-line reveal — gsap.test: SplitText lines, masked yPercent, stagger */
function addMultiline(el, tl, at) {
    const targets = multilineTargets(el);
    let offset = at;

    targets.forEach((target) => {
        if (!target.textContent.trim()) return;

        const split = new SplitText(target, {
            type: 'lines',
            linesClass: 'line-wrap',
        });

        split.lines.forEach((line) => {
            line.innerHTML = `<span>${line.innerHTML}</span>`;
        });

        const inners = target.querySelectorAll('.line-wrap > span');
        if (!inners.length) return;

        tl.from(
            inners,
            {
                yPercent: 110,
                opacity: 0,
                duration: 0.8,
                ease: 'power4.out',
                stagger: 0.15,
            },
            offset,
        );

        offset += 0.12;
    });
}

function setupHeaderScroll() {
    const header = document.querySelector('.promoter-socials-header');
    if (!header) return;

    const THRESHOLD = 80;
    const banner = header.querySelector('.promoter-socials-header__banner');
    const bar = header.querySelector('.promoter-socials-header__bar');
    if (!bar) return;

    const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
    const menu = bar.querySelector('.promoter-socials-menu');
    const ctaBtn = bar.querySelector('.promoter-socials-btn');

    const accentHexMap = {
        'social-yellow': '#EBE77A',
        'social-lime':   '#C4DE74',
        'social-orchid': '#8E2F6E',
        'social-violet': '#6a4790',
    };
    const accentHex = accentHexMap[header.dataset.accent] ?? '#EBE77A';

    function showScrolled() {
        if (banner) {
            gsap.to(banner, {
                height: 0,
                opacity: 0,
                duration: 0.4,
                delay: isDesktop() ? 0.7 : 0,
                ease: 'power2.inOut',
                overwrite: true,
            });
        }

        if (ctaBtn) {
            gsap.to(ctaBtn, { color: '#ffffff', duration: 0.3, ease: 'power2.out', overwrite: true });
        }

        if (isDesktop()) {
            if (menu) menu.style.flexWrap = 'nowrap';
            bar.style.width = 'fit-content';
            const pillWidth = bar.offsetWidth;
            bar.style.width = bar.parentElement.offsetWidth + 'px';
            bar.style.marginLeft = 'auto';
            bar.style.marginRight = 'auto';

            gsap.to(bar, {
                width: pillWidth,
                borderRadius: '9999px',
                backgroundColor: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(20px)',
                duration: 0.65,
                ease: 'power2.out',
                overwrite: true,
            });
        } else {
            bar.style.width = 'fit-content';
            const pillWidth = bar.offsetWidth;
            bar.style.width = bar.parentElement.offsetWidth + 'px';
            bar.style.marginLeft = 'auto';
            bar.style.marginRight = 'auto';

            gsap.to(bar, {
                width: pillWidth,
                borderRadius: '9999px',
                backgroundColor: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(20px)',
                duration: 0.65,
                ease: 'power2.out',
                overwrite: true,
            });
        }
    }

    function hideScrolled() {
        if (ctaBtn) {
            gsap.to(ctaBtn, {
                color: accentHex,
                duration: 0.3,
                delay: 0.3,
                ease: 'power2.out',
                overwrite: true,
                onComplete: () => gsap.set(ctaBtn, { clearProps: 'color' }),
            });
        }

        if (banner) {
            gsap.to(banner, {
                height: 'auto',
                opacity: 1,
                duration: 0.35,
                ease: 'power2.out',
                overwrite: true,
            });
        }

        if (isDesktop()) {
            gsap.to(bar, {
                width: bar.parentElement.offsetWidth,
                borderRadius: 0,
                backgroundColor: 'transparent',
                backdropFilter: 'blur(0px)',
                duration: 0.5,
                delay: 0.3,
                ease: 'power2.out',
                overwrite: true,
                onComplete: () => {
                    bar.style.width = '';
                    bar.style.marginLeft = '';
                    bar.style.marginRight = '';
                    if (menu) menu.style.flexWrap = '';
                    gsap.set(bar, { clearProps: 'width,borderRadius,backgroundColor,backdropFilter' });
                },
            });
        } else {
            gsap.to(bar, {
                width: bar.parentElement.offsetWidth,
                borderRadius: 0,
                backgroundColor: 'transparent',
                backdropFilter: 'blur(0px)',
                duration: 0.5,
                delay: 0.3,
                ease: 'power2.out',
                overwrite: true,
                onComplete: () => {
                    bar.style.width = '';
                    bar.style.marginLeft = '';
                    bar.style.marginRight = '';
                    gsap.set(bar, { clearProps: 'width,borderRadius,backgroundColor,backdropFilter' });
                },
            });
        }
    }

    let wasScrolled = false;
    let ticking = false;

    window.addEventListener(
        'scroll',
        () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const scrolled = window.scrollY > THRESHOLD;
                    if (scrolled !== wasScrolled) {
                        wasScrolled = scrolled;
                        scrolled ? showScrolled() : hideScrolled();
                    }
                    ticking = false;
                });
                ticking = true;
            }
        },
        { passive: true },
    );
}

/**
 * Programme grid items — fade up with stagger per grid, triggered on scroll.
 */
function setupProgrammeItems(root) {
    root.querySelectorAll('.ps-programme__grid').forEach((grid) => {
        const items = gsap.utils.toArray(grid.querySelectorAll('.ps-programme__item'));
        if (!items.length) return;

        gsap.from(items, {
            opacity: 0,
            y: 32,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.12,
            scrollTrigger: {
                trigger: grid,
                start: 'top 85%',
                toggleActions: 'play none none none',
                once: true,
            },
        });
    });
}

/**
 * Attendees swiper slides — fade up with stagger when the carousel scrolls into view.
 */
function setupAttendeesItems(root) {
    root.querySelectorAll('.ps-attendees-swiper').forEach((swiperEl) => {
        const slides = gsap.utils.toArray(swiperEl.querySelectorAll('.swiper-slide'));
        if (!slides.length) return;

        gsap.from(slides, {
            opacity: 0,
            y: 24,
            duration: 0.55,
            ease: 'power2.out',
            stagger: 0.1,
            scrollTrigger: {
                trigger: swiperEl,
                start: 'top 85%',
                toggleActions: 'play none none none',
                once: true,
            },
        });
    });
}

/**
 * Video grid/single items — fade up with stagger, triggered on scroll.
 * Uses [data-ps-video-item] stamped in PHP so LightGallery DOM changes don't interfere.
 * Groups items by parent so each section gets its own trigger.
 */
function setupVideoItems(root) {
    const allItems = gsap.utils.toArray(root.querySelectorAll('[data-ps-video-item]'));
    console.log('[PS] video items found:', allItems.length, allItems);
    if (!allItems.length) return;

    // Group by parent element so each gallery section gets its own ScrollTrigger
    const byParent = new Map();
    allItems.forEach((item) => {
        const parent = item.parentElement;
        if (!byParent.has(parent)) byParent.set(parent, []);
        byParent.get(parent).push(item);
    });

    byParent.forEach((items, parent) => {
        gsap.from(items, {
            opacity: 0,
            y: 28,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.12,
            scrollTrigger: {
                trigger: parent,
                start: 'top 85%',
                toggleActions: 'play none none none',
                once: true,
            },
        });
    });
}

/**
 * Videos ticker — GSAP infinite horizontal scroll, pauses on hover.
 * Items are duplicated in PHP; xPercent -50 loops back to the start seamlessly.
 */
function setupVideosTicker(root) {
    root.querySelectorAll('.ps-videos-ticker').forEach((ticker) => {
        const track = ticker.querySelector('.ps-videos-ticker__track');
        if (!track) return;

        requestAnimationFrame(() => {
            const tween = gsap.to(track, {
                xPercent: -50,
                ease: 'none',
                duration: 40,
                repeat: -1,
            });

            ticker.addEventListener('mouseenter', () => tween.pause());
            ticker.addEventListener('mouseleave', () => tween.play());
        });
    });
}

/**
 * FAQ accordion rows — fade in up on scroll, slight stagger between items.
 */
function setupAccordionFadeIn(root) {
    const items = gsap.utils.toArray(root.querySelectorAll('.accordion .accordion__item'));
    if (!items.length) return;

    gsap.set(items, { opacity: 0, y: 28 });

    gsap.to(items, {
        opacity: 1,
        y: 0,
        duration: 0.55,
        ease: 'power2.out',
        stagger: 0.1,
        scrollTrigger: {
            trigger: items[0].parentElement || items[0],
            start: 'top 85%',
            toggleActions: 'play none none none',
            once: true,
        },
    });
}

function addButtonFadeToTimeline(btn, tl, at) {
    tl.from(
        btn,
        {
            opacity: 0,
            duration: 0.5,
            ease: 'power2.out',
        },
        at,
    );
}

/**
 * Independent ticket buttons (header / no text siblings) — own ScrollTrigger.
 * Section CTAs are already on the text timeline via setupTextBlocks.
 */
function setupButtonFadeIn(root, sequencedButtons = new Set()) {
    root.querySelectorAll('a.promoter-socials-btn').forEach((btn) => {
        if (sequencedButtons.has(btn)) return;

        gsap.from(btn, {
            opacity: 0,
            duration: 0.5,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: btn,
                start: 'top 90%',
                toggleActions: 'play none none none',
                once: true,
            },
        });
    });
}

/**
 * Scroll indicator ring — rotates clockwise as the hero scrolls out of view,
 * scrolls to the next section on click, and has a magnetic follow on desktop.
 */
function setupScrollIndicators(root) {
    const desktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    root.querySelectorAll("[data-scroll-indicator='circle']").forEach((btn) => {
        const hero = btn.closest('section');
        if (!hero) return;

        // Click → scroll to next sibling section
        btn.addEventListener('click', () => {
            const target = hero.nextElementSibling || document.querySelector('#main');
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        // Ring scrub rotation
        const ring = btn.querySelector('[data-scroll-indicator-ring]');
        if (ring) {
            gsap.fromTo(
                ring,
                { rotation: 0 },
                {
                    rotation: 360,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: hero,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: true,
                    },
                },
            );
        }

        // Magnetic follow on desktop
        if (!desktop) return;

        const xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3.out' });
        const yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3.out' });

        btn.addEventListener('mousemove', (e) => {
            const r = btn.getBoundingClientRect();
            xTo((e.clientX - r.left - r.width / 2) * 0.4);
            yTo((e.clientY - r.top - r.height / 2) * 0.4);
        });

        btn.addEventListener('mouseleave', () => {
            xTo(0);
            yTo(0);
        });
    });
}

/**
 * Magnetic + rollover — desktop/pointer devices only.
 * Uses (hover: hover) and (pointer: fine) so it never fires on touch/mobile,
 * even in DevTools emulation where min-width checks can still pass.
 */
function setupMagneticButtons(root) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll('a.promoter-socials-btn').forEach((btn) => {
        const wrapper = btn.parentElement?.classList.contains('ps-inverted-corners') ? btn.parentElement : btn;
        const span = btn.querySelector('em span');

        const xTo = gsap.quickTo(wrapper, 'x', { duration: 0.4, ease: 'power3.out' });
        const yTo = gsap.quickTo(wrapper, 'y', { duration: 0.4, ease: 'power3.out' });

        btn.addEventListener('mouseenter', () => {
            if (span) gsap.to(span, { yPercent: -130, duration: 0.5, ease: 'power3.out', overwrite: true });
        });

        btn.addEventListener('mousemove', (e) => {
            const r = wrapper.getBoundingClientRect();
            xTo((e.clientX - r.left - r.width / 2) * MAGNETIC_STRENGTH);
            yTo((e.clientY - r.top - r.height / 2) * MAGNETIC_STRENGTH);
        });

        btn.addEventListener('mouseleave', () => {
            xTo(0);
            yTo(0);
            if (span) gsap.to(span, { yPercent: 0, duration: 0.5, ease: 'power3.out', overwrite: true });
        });
    });
}

/**
 * Event cards — fade up with stagger per grid, triggered on scroll.
 */
function setupEventCards(root) {
    root.querySelectorAll('.grid').forEach((grid) => {
        const cards = gsap.utils.toArray(grid.querySelectorAll('[data-ps-card]'));
        if (!cards.length) return;

        gsap.from(cards, {
            opacity: 0,
            y: 32,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.12,
            scrollTrigger: {
                trigger: grid,
                start: 'top 85%',
                toggleActions: 'play none none none',
                once: true,
            },
        });
    });
}

/**
 * Quote block — pinned word-fill scrub.
 * Splits quote text into word spans (opacity 0.15), pins the section,
 * then scrubs opacity to 1 word-by-word as the user scrolls.
 */
function setupQuoteWordFill(root) {
    root.querySelectorAll('[data-ps-word-fill]').forEach((el) => {
        const section = el.closest('.ps-quote-section');
        if (!section) return;

        const text = el.textContent.trim();
        el.textContent = '';

        const words = text.split(/\s+/).filter(Boolean).map((word, i, arr) => {
            const span = document.createElement('span');
            span.textContent = word;
            span.style.cssText = 'display:inline-block;opacity:0.15';
            el.appendChild(span);
            if (i < arr.length - 1) el.appendChild(document.createTextNode(' '));
            return span;
        });

        if (!words.length) return;

        gsap.to(words, {
            opacity: 1,
            ease: 'none',
            stagger: 0.4,
            scrollTrigger: {
                trigger: el,
                start: 'top 85%',
                end: 'top 10%',
                scrub: true,
            },
        });
    });
}

/**
 * Quote block image — clip-path wipe reveal left → right on scroll.
 */
function setupQuoteImageReveal(root) {
    root.querySelectorAll('[data-ps-wipe]').forEach((el) => {
        gsap.fromTo(
            el,
            { clipPath: 'inset(0 100% 0 0)' },
            {
                clipPath: 'inset(0 0% 0 0)',
                ease: 'power2.inOut',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 80%',
                    end: 'top 20%',
                    scrub: true,
                },
            },
        );
    });
}

// ─── PS Event cards ───────────────────────────────────────────────────────────

function psMeasureCard(card) {
    const front = card.querySelector('.card__front');
    const back = card.querySelector('.card__back');
    if (!front || !back) return 0;
    const pf = front.style.position;
    const pb = back.style.position;
    front.style.position = back.style.position = 'relative';
    void front.offsetHeight;
    const h = Math.max(front.scrollHeight, back.scrollHeight);
    front.style.position = pf || 'absolute';
    back.style.position = pb || 'absolute';
    return h;
}

function psEqualizeGrid(cards) {
    cards.forEach((card) => {
        const wrap = card.querySelector('.card__content');
        if (wrap) wrap.style.height = 'auto';
    });
    const maxH = Math.max(...cards.map(psMeasureCard));
    if (maxH > 0) {
        cards.forEach((card) => {
            const wrap = card.querySelector('.card__content');
            if (wrap) wrap.style.height = maxH + 'px';
        });
    }
}

function psGetGrids() {
    const grids = new Map();
    document.querySelectorAll('[data-ps-card]').forEach((card) => {
        const grid = card.closest('.grid') ?? card.parentElement;
        if (!grids.has(grid)) grids.set(grid, []);
        grids.get(grid).push(card);
    });
    return grids;
}

function psInitCards() {
    psGetGrids().forEach((cards) => {
        const imgs = [];
        cards.forEach((card) => imgs.push(...card.querySelectorAll('img')));

        if (!imgs.length) {
            psEqualizeGrid(cards);
            return;
        }

        let loaded = 0;
        const total = imgs.length;
        const onLoad = () => {
            if (++loaded === total) psEqualizeGrid(cards);
        };
        imgs.forEach((img) => {
            if (img.complete) {
                onLoad();
            } else {
                img.addEventListener('load', onLoad, { once: true });
                img.addEventListener('error', onLoad, { once: true });
            }
        });
    });
}

function psDebounce(fn, wait) {
    let t;
    return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
    };
}

window.flipToBackById = function (id) {
    const card = document.getElementById(id);
    if (!card) return;
    requestAnimationFrame(() => {
        const content = card.querySelector('.card__content');
        const front = card.querySelector('.card__front');
        const back = card.querySelector('.card__back');
        if (content) content.style.transform = 'rotateY(180deg)';
        if (front) { front.style.zIndex = '1'; front.style.pointerEvents = 'none'; }
        if (back) { back.style.zIndex = '3'; back.style.pointerEvents = 'auto'; }
    });
};

window.flipToFrontById = function (id) {
    const card = document.getElementById(id);
    if (!card) return;
    requestAnimationFrame(() => {
        const content = card.querySelector('.card__content');
        const front = card.querySelector('.card__front');
        const back = card.querySelector('.card__back');
        if (content) content.style.transform = '';
        if (front) { front.style.zIndex = '2'; front.style.pointerEvents = 'auto'; }
        if (back) { back.style.zIndex = '1'; back.style.pointerEvents = 'none'; }
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', psInitCards);
} else {
    psInitCards();
}
window.addEventListener('resize', psDebounce(psInitCards, 150));

// Event share toggle
document.addEventListener('click', (e) => {
    const shareBtn = e.target.closest('.event-share');
    if (!shareBtn) {
        if (!e.target.closest('.event-share-menu')) {
            document.querySelectorAll('.event-share-menu.open').forEach((m) => m.classList.remove('open'));
        }
        return;
    }
    e.stopPropagation();
    const menu = shareBtn.closest('.share')?.querySelector('.event-share-menu');
    if (!menu) return;
    const isOpen = menu.classList.contains('open');
    document.querySelectorAll('.event-share-menu.open').forEach((m) => m.classList.remove('open'));
    if (!isOpen) {
        menu.classList.add('open');
        const card = shareBtn.closest('[data-ps-card]');
        if (card) {
            const close = () => { menu.classList.remove('open'); card.removeEventListener('mouseleave', close); };
            card.addEventListener('mouseleave', close);
        }
    }
});

// Calendar toggle
document.addEventListener('click', (e) => {
    const btn = e.target.closest('.ps-cal-btn');
    if (btn) {
        e.stopPropagation();
        const wrapper = btn.closest('.ps-cal');
        const list = wrapper?.querySelector('.ps-cal-list');
        if (!list) return;
        const isOpen = list.classList.contains('open');
        document.querySelectorAll('.ps-cal-list.open').forEach((l) => l.classList.remove('open'));
        if (!isOpen) list.classList.add('open');
        return;
    }
    if (!e.target.closest('.ps-cal')) {
        document.querySelectorAll('.ps-cal-list.open').forEach((l) => l.classList.remove('open'));
    }
});
